<template>
  <div>
    <CommonBreadCrumbs :data="breadcrumbs"></CommonBreadCrumbs>
    <div>
      <NuxtLayout name="center" id="page-content">
        <h1>{{ contentData.title }}</h1>
        <div
          class="flex md:my-16 my-8 md:mx-4 lg:mx-8 text-dark-blue md:space-x-12 lg:space-x-[107px] rtl:space-x-reverse"
        >
          <div class="hidden md:block w-full md:max-w-[240px] lg:max-w-[300px]">
            <BaseTableOfContent :content-ref="conditionContent">
              <NuxtLinkLocale :to="`/tests` + '?condition=' + contentData.infection.slug" :locale="switchBlogLocale()">
                <BaseButton
                  :text="$t('condition.viewAll', { testName: contentData.title })"
                  class="w-full min-h-12 text-lg font-semibold !whitespace-break-spaces"
                />
              </NuxtLinkLocale>
            </BaseTableOfContent>
          </div>
          <div class="w-full">
            <article>
              <NuxtImg
                v-if="contentData.media == 'image' && contentData.image?.permalink"
                loading="lazy"
                :src="contentData.image.permalink"
                :alt="contentData.image?.alt"
                class="rounded aspect-video object-cover w-full md:h-[310px] max-h-full h-[358px]"
              />
              <video
                v-if="contentData.media == 'video' && contentData.video?.permalink"
                ref="videoPlayer"
                muted
                controls
                controlsList="nodownload"
                :poster="contentData.video_placeholder?.permalink"
                class="rounded aspect-video md:object-cover w-full max-h-full"
              >
                <source :src="contentData.video.permalink" type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <div ref="conditionContent" class="condition-content">
                <div v-if="contentData.intro_text" v-html="contentData.intro_text" class="intro-text my-15"></div>
                <div ref="headers" v-for="(sectionContent, index) in contentData.body_text" :key="index">
                  <div v-if="sectionContent.type == 'text'" v-html="sectionContent.text" class="mb-1"></div>
                  <div v-if="sectionContent.type == 'did_you_know'">
                    <CommonDidYouKnow :data="sectionContent.text"></CommonDidYouKnow>
                  </div>
                  <div
                    v-if="sectionContent.type == 'view_test' && sectionContent.display_view_tests"
                    class="flex justify-center mt-12"
                  >
                    <NuxtLinkLocale
                      :to="`/tests` + '?condition=' + contentData.infection.slug"
                      :locale="switchBlogLocale()"
                    >
                      <BaseButton
                        :text="
                          $t('condition.viewAll', {
                            testName: contentData.title,
                          })
                        "
                        class="w-full min-h-12 !max-w-xs text-lg font-semibold !whitespace-break-spaces"
                      />
                    </NuxtLinkLocale>
                  </div>
                </div>
                <div v-if="contentData.display_factsheet" class="py-8">
                  <CommonPdfBLock
                    :pdfTitle="contentData.factsheet_title"
                    :pdfFile="contentData.factsheet_attachment"
                  ></CommonPdfBLock>
                </div>
                <ClinicHelpBlock color="primary/10" class="mt-16 lg:mt-24 space-y-6"></ClinicHelpBlock>
              </div>
            </article>
          </div>
        </div>
      </NuxtLayout>

      <div class="bg-secondary-lighter" v-if="products?.length > 0">
        <NuxtLayout name="center" class="pt-12 md:pt-20 pb-12">
          <CommonMostPopularTests
            :products="products"
            :title="$t('condition.popularTests', { testName: contentData.title })"
            view-detail-button-title=""
            :view-all-button-title="$t('condition.viewAll', { testName: contentData.title })"
            :view-all-url="`/tests` + '?condition=' + contentData.infection.slug"
          ></CommonMostPopularTests>
        </NuxtLayout>
      </div>

      <NuxtLayout name="center">
        <CommonLiveChatBlock class="mb-10 md:mb-16 lg:mb-24"></CommonLiveChatBlock>
      </NuxtLayout>

      <ClinicCTABlock imgSrc="USPs_graphic_03_1" :getStarted="true"></ClinicCTABlock>
      <div id="display-progressbar" class="w-0 h-0"></div>
    </div>
  </div>
</template>

<script setup>
const route = useRoute();
const contentData = ref();
const tableList = ref();
const products = ref();
const conditionContent = ref(null);

const props = defineProps({
  param_name: {
    type: String,
    default: "slug",
  },
});

let route_params = route.params;
let slug = route_params[props.param_name];

const { data: results, pending, refresh } = await useConditionDetail(slug);
contentData.value = results.value?.data;
if (!contentData.value) {
  throw createError({ statusCode: 404, statusMessage: "Page Not Found" });
}
products.value = contentData.value.most_popular_tests;
const headers = ref(null);

onMounted(() => {
  titleContent();
});

watch(
  () => slug,
  () => {
    refresh();
  }
);

function titleContent() {
  let result;
  const regex = /<h3>(.*?)<\/h3>/g; //const regex = /(?<=\<h3>).+?(?=\<\/h3>)/gm;
  result = (contentData.value.body_text ?? []).map((data) => (data.type == "text" ? data.text.match(regex) : ""));
  tableList.value = [].concat(...result);
  tableList.value = tableList.value
    .filter((filteredData) => filteredData !== null)
    .map((data) => data.replace(/<\/?h3>/g, ""));
}

useMetaTitles(contentData.value, "condition");

const breadcrumbs = getBreadcrumbs("condition", contentData.value);
</script>

<style scoped>
.section-topic a.active {
  font-weight: bold;
}
</style>
